/**
 * Browser wiring for the expired-media self-heal (see media-url-self-heal.ts).
 *
 *  - a capturing `error` listener on `document` catches every <img>/<video>/
 *    <audio>/<source> that fails to load (load errors do not bubble, hence
 *    capture) and re-signs it once;
 *  - a sweep on tab focus / visibility, and every 5 minutes while visible,
 *    refreshes anything within 15 minutes of expiry before it can break.
 *
 * Installed once from useInit. Idempotent.
 */

import { posthogClient } from 'services/posthog/posthog.service'
// Static on purpose: a runtime `import('openapi')` here manufactured a chunk
// that only loaded on the first expired image, so a tab on a pre-deploy bundle
// could not self-heal at all (see stale-bundle.ts).
import { OpenAPI } from '../../../openapi/core/OpenAPI'
import { request } from '../../../openapi/core/request'
import {
  createMediaUrlHealer,
  type FreshUrlFetcher,
  type MediaElementLike,
  type MediaUrlHealer,
} from './media-url-self-heal'

const SWEEP_INTERVAL_MS = 5 * 60 * 1000
const FOCUS_DEBOUNCE_MS = 500
const MEDIA_TAGS = new Set(['IMG', 'VIDEO', 'AUDIO', 'SOURCE'])

const fetchFresh: FreshUrlFetcher = async (attachmentId, variant) => {
  return request<{ url: string; expiresAt: string }>(OpenAPI, {
    method: 'GET',
    url: '/v1/media/{id}/url',
    path: { id: attachmentId },
    query: variant === 'thumbnail' ? { variant } : undefined,
  })
}

let installed: (() => void) | null = null
let healer: MediaUrlHealer | null = null

/** The shared healer (also used by useAttachmentUrl to keep its cache honest). */
export function getMediaUrlHealer(): MediaUrlHealer {
  if (!healer) {
    healer = createMediaUrlHealer({
      fetchFresh,
      onHealed: event => {
        posthogClient.capture('media_url_self_healed', {
          reason: event.reason,
          variant: event.variant,
          was_expired: event.wasExpired,
        })
      },
      onFailed: (attachmentId, error) => {
        console.warn('[media-self-heal] could not refresh', attachmentId, error)
      },
    })
  }
  return healer
}

export function installMediaUrlSelfHeal(): () => void {
  if (installed) return installed
  if (typeof document === 'undefined') return () => {}
  const h = getMediaUrlHealer()

  const onError = (event: Event) => {
    const target = event.target as (EventTarget & { tagName?: string }) | null
    if (!target || !target.tagName || !MEDIA_TAGS.has(target.tagName)) return
    void h.healElement(target as unknown as MediaElementLike, 'error')
  }
  document.addEventListener('error', onError, true)

  let debounce: ReturnType<typeof setTimeout> | undefined
  const sweepSoon = () => {
    if (debounce) clearTimeout(debounce)
    debounce = setTimeout(() => {
      debounce = undefined
      if (document.hidden) return
      void h.sweep(document)
    }, FOCUS_DEBOUNCE_MS)
  }
  const onVisibility = () => {
    if (!document.hidden) sweepSoon()
  }
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('focus', sweepSoon)
  const interval = setInterval(() => {
    if (!document.hidden) void h.sweep(document)
  }, SWEEP_INTERVAL_MS)

  installed = () => {
    document.removeEventListener('error', onError, true)
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('focus', sweepSoon)
    clearInterval(interval)
    if (debounce) clearTimeout(debounce)
    installed = null
  }
  return installed
}
